/**
 * Notification service — in-app alerts for owners and admins.
 *
 * Backend endpoints:
 *   GET   /notifications            list + unread count
 *   PATCH /notifications/:id/read   mark one read
 *   PATCH /notifications/read-all   mark all read
 *
 * Falls back to demo data only when no API URL is configured (preview mode);
 * with a live API, failures propagate to the caller.
 */

import { apiGetAuth, apiPatchAuth, isApiConfigured } from "./http";
import { demoNotifications } from "@/mocks/notifications";

export interface PortalNotification {
  id:        number;
  type:      string;
  title:     string;
  body:      string | null;
  link:      string | null;
  isRead:    boolean | number;
  createdAt: string;
}

export interface NotificationResult {
  data:        PortalNotification[];
  unreadCount: number;
}

function demoResult(): NotificationResult {
  const data = demoNotifications as PortalNotification[];
  return { data, unreadCount: data.filter((n) => !n.isRead).length };
}

/** Fetch the latest notifications for the signed-in user. */
export async function getNotifications(
  token: string | null,
  limit = 20,
): Promise<NotificationResult> {
  if (isApiConfigured() && token) {
    const res = await apiGetAuth<{ data: PortalNotification[]; unreadCount: number }>(
      `/notifications?limit=${limit}`,
      token,
    );
    return { data: res.data ?? [], unreadCount: res.unreadCount ?? 0 };
  }
  await new Promise((resolve) => setTimeout(resolve, 200));
  return demoResult();
}

/** Mark a single notification as read. */
export async function markNotificationRead(token: string | null, id: number): Promise<void> {
  if (isApiConfigured() && token) {
    await apiPatchAuth<{ id: number }>(`/notifications/${id}/read`, {}, token);
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, 160));
}

/** Mark every notification as read. */
export async function markAllNotificationsRead(token: string | null): Promise<void> {
  if (isApiConfigured() && token) {
    await apiPatchAuth<null>("/notifications/read-all", {}, token);
    return;
  }
  await new Promise((resolve) => setTimeout(resolve, 220));
}